import React from 'react';
import { EmailTemplate } from '../../types';
import { useI18n } from '../../context/I18nContext';
import ActionIconButton from '../ui/ActionIconButton';
import EmptyState from '../ui/EmptyState';
import ScrollFadeContainer from '../ui/ScrollFadeContainer';
import Spinner from '../ui/Spinner';
import TruncateTooltip from '../ui/TruncateTooltip';
import { leadStatusColors } from '../../styles/colors';
import { Copy, Edit, Mail, Trash2 } from 'lucide-react';

const TEMPLATE_TYPES: EmailTemplate['type'][] = [
  'lead_generation',
  'follow_up',
  'negotiation',
  'delivery',
];

export type TemplateTypeFilter = 'all' | EmailTemplate['type'];

const TEMPLATE_TYPE_LABEL_KEYS: Record<EmailTemplate['type'], string> = {
  lead_generation: 'messages.typeShort.leadGeneration',
  follow_up: 'messages.typeShort.followUp',
  negotiation: 'messages.typeShort.negotiation',
  delivery: 'messages.typeShort.delivery',
};

const filterChipClass = (active: boolean) =>
  `shrink-0 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${
    active
      ? 'bg-primary-600 text-white shadow-sm'
      : 'bg-gray-100 text-gray-600 hover:bg-gray-200/80'
  }`;

interface TemplatesPickerProps {
  templates: EmailTemplate[];
  filteredTemplates: EmailTemplate[];
  loading: boolean;
  error: string | null | undefined;
  typeFilter: TemplateTypeFilter;
  onTypeFilterChange: (filter: TemplateTypeFilter) => void;
  activeTemplateId: string | null;
  onApply: (template: EmailTemplate) => void;
  onEdit: (template: EmailTemplate) => void;
  onDelete: (id: string) => void;
  onCopy: (template: EmailTemplate) => void;
  onCreate: () => void;
  /** Desktop sidebar: fade scroll region. Sheet/stack: plain list. */
  scrollable?: boolean;
}

const TemplatesPicker: React.FC<TemplatesPickerProps> = ({
  templates,
  filteredTemplates,
  loading,
  error,
  typeFilter,
  onTypeFilterChange,
  activeTemplateId,
  onApply,
  onEdit,
  onDelete,
  onCopy,
  onCreate,
  scrollable = false,
}) => {
  const { t } = useI18n();

  const getTemplateTypeBadge = (type: EmailTemplate['type']) => {
    const typeConfig: Record<EmailTemplate['type'], { label: string; colorClass: string }> = {
      lead_generation: {
        label: t('messages.type.leadGeneration'),
        colorClass: leadStatusColors.to_contact,
      },
      follow_up: {
        label: t('messages.type.followUp'),
        colorClass: leadStatusColors.pending,
      },
      negotiation: {
        label: t('messages.type.negotiation'),
        colorClass: leadStatusColors.negotiation,
      },
      delivery: {
        label: t('messages.type.delivery'),
        colorClass: leadStatusColors.completed,
      },
    };

    const config = typeConfig[type] || {
      label: type,
      colorClass: leadStatusColors.archived,
    };

    return (
      <span
        className={`inline-flex items-center shrink-0 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${config.colorClass}`}
      >
        {config.label}
      </span>
    );
  };

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 font-medium">{t('common.error')}</p>
        <p className="text-gray-500 mt-1">{error}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="py-10 flex justify-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <EmptyState
        icon={Mail}
        title={t('messages.noTemplates')}
        description={t('messages.noTemplatesDescription')}
        actionLabel={t('messages.createFirst')}
        onAction={onCreate}
      />
    );
  }

  const templateCards = filteredTemplates.map((template) => {
    const isActiveTemplate = activeTemplateId === template.id;
    return (
      <div
        key={template.id}
        role="button"
        tabIndex={0}
        aria-label={
          isActiveTemplate
            ? `${t('messages.deselectTemplate')} ${template.name}`
            : `${t('messages.useTemplate')} ${template.name}`
        }
        aria-pressed={isActiveTemplate}
        className={`px-3 py-2.5 rounded-xl border transition-all duration-200 group cursor-pointer ${
          isActiveTemplate
            ? 'border-primary-300 bg-primary-50/70 shadow-sm ring-1 ring-primary-200/70'
            : 'border-gray-200/60 bg-white/50 hover:bg-white/80 hover:border-gray-300/60 hover:shadow-md'
        }`}
        onClick={() => onApply(template)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onApply(template);
          }
        }}
      >
        <div className="flex items-start gap-1.5">
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex items-center gap-2 min-w-0">
              <TruncateTooltip
                text={template.name}
                className={`font-medium text-sm ${
                  isActiveTemplate ? 'text-primary-900' : 'text-gray-900'
                }`}
              />
            </div>
            <div>{getTemplateTypeBadge(template.type)}</div>
          </div>
          <div
            className="flex shrink-0 items-center gap-0.5"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <ActionIconButton
              icon={Copy}
              onClick={(e) => {
                e?.stopPropagation();
                onCopy(template);
              }}
              variant="ghost"
              size="sm"
              tooltip={t('messages.copyTemplate')}
            />
            <ActionIconButton
              icon={Edit}
              onClick={(e) => {
                e?.stopPropagation();
                onEdit(template);
              }}
              variant="edit"
              size="sm"
              tooltip={t('common.edit')}
            />
            <ActionIconButton
              icon={Trash2}
              onClick={(e) => {
                e?.stopPropagation();
                onDelete(template.id);
              }}
              variant="delete"
              size="sm"
              tooltip={t('common.delete')}
            />
          </div>
        </div>
      </div>
    );
  });

  return (
    <div className={`flex flex-col ${scrollable ? 'min-h-0 flex-1' : ''}`}>
      <div
        className="shrink-0 flex flex-wrap gap-1.5"
        role="tablist"
        aria-label={t('messages.filterByType')}
      >
        <button
          type="button"
          role="tab"
          aria-selected={typeFilter === 'all'}
          className={filterChipClass(typeFilter === 'all')}
          onClick={() => onTypeFilterChange('all')}
        >
          {t('messages.filterAll')}
        </button>
        {TEMPLATE_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            role="tab"
            aria-selected={typeFilter === type}
            className={filterChipClass(typeFilter === type)}
            onClick={() => onTypeFilterChange(type)}
          >
            {t(TEMPLATE_TYPE_LABEL_KEYS[type])}
          </button>
        ))}
      </div>
      {filteredTemplates.length === 0 ? (
        <EmptyState
          icon={Mail}
          title={t('messages.noTemplatesForFilter')}
          description={t('messages.noTemplatesForFilterDescription')}
          actionLabel={t('messages.clearTypeFilter')}
          onAction={() => onTypeFilterChange('all')}
        />
      ) : scrollable ? (
        <ScrollFadeContainer
          className="mt-3 -mx-3 sm:-mx-4 xl:-mx-6 flex-1 min-h-0"
          scrollClassName="px-3 sm:px-4 xl:px-6 space-y-3 pb-3"
        >
          {templateCards}
        </ScrollFadeContainer>
      ) : (
        <div className="mt-3 space-y-3 pb-1">{templateCards}</div>
      )}
    </div>
  );
};

export default TemplatesPicker;
